import React, {useMemo, useState} from 'react'
import styled from "styled-components";
import {Button, Calendar, DatePicker, Form, Input, Popover, Space} from "antd";
import {ListHeader, SearchRow, SearchWrap} from "../../Components";
import dayjs, {Dayjs} from "dayjs";
import {useNavigate} from "react-router-dom";
import {useQuery} from "react-query";
import {REPO} from "../../Repository/repo";
import {useStore} from "../../Hooks";
import {queryKeys} from "../../Constant/QueryKeys";
import {VACATION_CALENDAR_CHIP} from "../../Constant/Vacation";
import {VacationCalendarItem} from "../../Model/VacationModel";
import useUser from "../../Hooks/useUser";
import useHolidays from "../../Hooks/useHolidays";

const Wrap = styled.div`
  .vac-cal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    margin-bottom: 10px;
    h3 {
      margin: 0;
    }
  }
  .vac-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 0;
  }
  .holiday-date {
    color: #f03e3e;
  }
  .holiday-label {
    color: #f03e3e;
    font-size: 11px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vac-chip {
    color: white;
    font-size: 11px;
    border-radius: 4px;
    padding: 1px 6px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }
  .vac-more {
    font-size: 11px;
    color: #1677ff;
    cursor: pointer;
  }
`

const MAX_CHIPS_PER_CELL = 4

const VacationCalendar: React.FC = () => {
    const {UIStore} = useStore()
    const {userData} = useUser()
    const {isHoliday, holidayName} = useHolidays()
    const isHrAdmin = !!userData?.data?.is_hr_admin
    const navigate = useNavigate()
    const [date, setDate] = useState<Dayjs>(dayjs())
    const [keyword, setKeyword] = useState<string>("")

    const year = date.year()
    const month = date.month() + 1

    // 휴가는 더이상 지점 컨텍스트가 없음 — 전사 단일 API
    const {data} = useQuery(
        [...queryKeys.vacationCalendar, year, month],
        async () => {
            const res = await REPO.Vacation.GetCalendar({year, month})
            return res.data
        }
    )

    const itemsByDate = useMemo(() => {
        const map: Record<string, VacationCalendarItem[]> = {}
        const list = data?.list || []
        const filtered = keyword
            ? list.filter(it => it.employee_name?.includes(keyword))
            : list
        // 시작~종료 사이 모든 날짜에 칩을 펼침
        filtered.forEach(it => {
            let cur = dayjs(it.vacation_date_start)
            const end = dayjs(it.vacation_date_end)
            while (cur.isBefore(end) || cur.isSame(end, 'day')) {
                const key = cur.format("YYYY-MM-DD")
                if (!map[key]) map[key] = []
                map[key].push(it)
                cur = cur.add(1, 'day')
            }
        })
        return map
    }, [data, keyword])

    const labelOf = (it: VacationCalendarItem) => {
        const fraction = parseFloat(String((it as any).vacation_unit_fraction ?? 1))
        if (fraction === 0.5 && it.vacation_period_label === "오전") return "반차(오전)"
        if (fraction === 0.5 && it.vacation_period_label === "오후") return "반차(오후)"
        return "휴가"
    }
    const colorOf = (label: string) => label.startsWith("반차") ? VACATION_CALENDAR_CHIP.반차 : VACATION_CALENDAR_CHIP.휴가

    const goToDocument = (it: VacationCalendarItem) => {
        navigate(`/Vacation/Approval/${it.vacation_approval_seq}`)
    }

    const chipPopover = (it: VacationCalendarItem, label: string) => (
        <div style={{padding: "4px 0"}}>
            <div style={{marginBottom: 8}}>{it.employee_name} · {label}</div>
            <Button size={"small"} type={"primary"} onClick={() => goToDocument(it)}>문서로 이동</Button>
        </div>
    )

    const cellRender = (current: Dayjs) => {
        if (current.month() !== date.month()) return null
        const key = current.format("YYYY-MM-DD")
        const items = itemsByDate[key] || []
        if (!items.length) return null
        const visible = items.slice(0, MAX_CHIPS_PER_CELL)
        const overflow = items.length - visible.length

        return (
            <div className={"vac-cell"}>
                {visible.map((it, idx) => {
                    const label = labelOf(it)
                    const color = colorOf(label)
                    const chip = (
                        <div
                            key={idx}
                            className={"vac-chip"}
                            style={{background: color, cursor: isHrAdmin ? "pointer" : "default"}}
                            title={`${it.employee_name} · ${label}`}
                            onClick={isHrAdmin ? (e) => e.stopPropagation() : undefined}
                        >
                            {it.employee_name} · {label}
                        </div>
                    )
                    return isHrAdmin ? (
                        <Popover key={idx} content={chipPopover(it, label)} trigger={"click"}>
                            {chip}
                        </Popover>
                    ) : chip
                })}
                {overflow > 0 && (
                    <Popover
                        content={
                            <div style={{maxWidth: 280}}>
                                {items.map((it, idx) => {
                                    const label = labelOf(it)
                                    return (
                                        <div key={idx} style={{padding: "4px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}>
                                            <span>
                                                <span style={{
                                                    display: "inline-block",
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: 4,
                                                    background: colorOf(label),
                                                    marginRight: 6
                                                }}/>
                                                {it.employee_name} · {label}
                                            </span>
                                            {isHrAdmin && (
                                                <Button size={"small"} onClick={() => goToDocument(it)}>이동</Button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        }
                        title={current.format("YYYY-MM-DD")}
                        trigger={"click"}
                    >
                        <span className={"vac-more"}>+{overflow}건</span>
                    </Popover>
                )}
            </div>
        )
    }

    // antd 기본 셀 마크업을 복제하면서 토/일/공휴일 날짜를 빨강 + 공휴일명 표시
    const fullCellRender = (current: Dayjs) => {
        const inView = current.month() === date.month()
        const isToday = dayjs().isSame(current, 'day')
        const name = inView ? holidayName(current) : undefined
        const isRed = inView && (current.day() === 0 || current.day() === 6 || isHoliday(current))
        return (
            <div className={`ant-picker-cell-inner ant-picker-calendar-date${isToday ? " ant-picker-calendar-date-today" : ""}`}>
                <div className={`ant-picker-calendar-date-value${isRed ? " holiday-date" : ""}`} title={name}>
                    {String(current.date()).padStart(2, "0")}
                </div>
                <div className={"ant-picker-calendar-date-content"}>
                    {name && <div className={"holiday-label"} title={name}>{name}</div>}
                    {cellRender(current)}
                </div>
            </div>
        )
    }

    return (
        <Wrap>
            <SearchWrap onFinish={() => { /* keyword 는 즉시 반영 */ }}>
                <SearchRow>
                    <Form.Item label={"직원명"}>
                        <Input
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder={"직원 이름 검색"}
                            allowClear
                        />
                    </Form.Item>
                </SearchRow>
            </SearchWrap>
            <ListHeader>
                <div/>
                <div>
                    <Button type={"primary"} onClick={() => navigate("/Vacation/Request/Create")}>
                        휴가신청서 작성
                    </Button>
                </div>
            </ListHeader>
            <Calendar
                value={date}
                onPanelChange={(d) => setDate(d)}
                dateFullCellRender={fullCellRender}
                headerRender={({value, onChange}) => (
                    <div className={"vac-cal-header"}>
                        <h3>{value.format("YYYY년 MM월")}</h3>
                        <div>
                            <Button style={{marginRight: "10px"}} onClick={() => onChange(value.clone().subtract(1, 'month'))}>이전</Button>
                            <DatePicker
                                picker={"month"}
                                value={value}
                                onChange={(v) => v && onChange(v as Dayjs)}
                                allowClear={false}
                            />
                            <Button style={{marginLeft: "10px"}} onClick={() => onChange(value.clone().add(1, 'month'))}>다음</Button>
                        </div>
                    </div>
                )}
            />
        </Wrap>
    )
}

export default VacationCalendar
